import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { onSnapshot, doc } from "firebase/firestore";
import { db } from "./firebase";
import { BrandingConfig } from "@/types";

const DEFAULTS: BrandingConfig = {
  primaryColor: "#0071e3",
  portalName: "HelpDesk",
};

const BrandingContext = createContext<BrandingConfig>(DEFAULTS);

export function useBranding() {
  return useContext(BrandingContext);
}

// Pick black or white text to read against the supplied hex background,
// per WCAG relative-luminance formula. Anything ≥ 0.5 luminance gets
// dark text; below gets white. Used to keep .btn-primary readable when
// admins set a light brand color (e.g. orange #ff8647 → dark text).
function readableTextOn(bg: string): string {
  const m = bg.match(/^#?([0-9a-f]{6})$/i);
  if (!m) return "#ffffff";
  const hex = m[1];
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  const lin = (c: number) => {
    const v = c / 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  };
  const L = 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
  return L > 0.5 ? "#1a1a1a" : "#ffffff";
}

export function BrandingProvider({ children }: { children: ReactNode }) {
  const [branding, setBranding] = useState<BrandingConfig>(DEFAULTS);

  useEffect(() => {
    const unsub = onSnapshot(doc(db, "config", "branding"), (snap) => {
      if (!snap.exists()) return;
      const data = snap.data() as Partial<BrandingConfig>;
      setBranding({
        primaryColor: data.primaryColor || DEFAULTS.primaryColor,
        portalName: data.portalName || DEFAULTS.portalName,
        logoUrl: data.logoUrl,
      });
    });
    return unsub;
  }, []);

  useEffect(() => {
    document.documentElement.style.setProperty("--blue", branding.primaryColor);
    // --blue-text picks white or near-black so .btn-primary text stays
    // legible regardless of the brand color the admin chose.
    document.documentElement.style.setProperty("--blue-text", readableTextOn(branding.primaryColor));
    // Per-page <title> is set by each route; this only sets the
    // initial title before any route mounts.
    if (!document.title || document.title === "TicketWhip" || document.title === "HelpDesk") {
      document.title = branding.portalName;
    }
  }, [branding.primaryColor, branding.portalName]);

  return <BrandingContext.Provider value={branding}>{children}</BrandingContext.Provider>;
}
